import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IconArrowLeft } from "@tabler/icons-react";
import { ApiConstant, PathConstant } from "const";
import { MobileDeviceIcon, MacbookDeviceIcon, WindowsDeviceIcon } from "theme/images";
import { ApiUtils, HumpUtils } from "utils";
import { ConfirmDialog, CircleLoading } from "components";
import { mapApiDeviceToRow, Device, formatDateTime } from "./deviceManagement.helper";
import useDeviceActions from "./hooks/useDeviceActions";
import { useTranslation } from "react-i18next";

interface AccountDetail {
  mail?: string;
  address?: string;
  unitName?: string;
  departmentName?: string;
  positionName?: string;
}

interface DeviceDetailProps {
  onRefreshList?: () => void;
}

interface LocationState {
  device?: Device;
}

const DeviceDetail: React.FC<DeviceDetailProps> = ({ onRefreshList }) => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const searchParams = new URLSearchParams(location.search);
  const deviceId = searchParams.get("detail");
  const [device, setDevice] = useState<Device | null>(location.state?.device || null);
  const [accountDetail, setAccountDetail] = useState<AccountDetail | null>(null);
  const [loading, setLoading] = useState(!device);
  const { t } = useTranslation("deviceManagement");

  const handleGoBack = () => {
    const params = new URLSearchParams(location.search);
    params.delete("detail");
    history.push({
      pathname: (PathConstant as any).DEVICE_MANAGEMENT,
      search: params.toString() ? `?${params.toString()}` : "",
    });
    if (onRefreshList) onRefreshList();
  };

  const {
    confirmDialog,
    openBlockDialog,
    openUnblockDialog,
    openLogoutDialog,
    closeDialog,
    confirmAction,
    getDialogTitle,
  } = useDeviceActions({
    onSuccess: handleGoBack,
  });

  // Fetch device if location.state is lost (e.g., F5 refresh)
  useEffect(() => {
    if (device) return;
    if (!deviceId) return;

    const fetchDevice = async () => {
      setLoading(true);
      try {
        const response = await (ApiUtils as any).createApiWithToken(false).get(
          (ApiConstant as any).DEVICES_FILTER,
          HumpUtils.decamelizeKeys({
            deviceId: deviceId,
            paging: 1,
            limit: 100,
            offset: 0,
          })
        );

        if (response.status === (ApiConstant as any).STT_OK) {
          const rawData = response.data;
          const pageDto = HumpUtils.camelizeKeys(rawData.data);
          const groups = pageDto.data || [];

          for (const group of groups) {
            const devices = group.devices || [];
            const found = devices.find((d: any) => d.id === deviceId);
            if (found) {
              const mapped = mapApiDeviceToRow(found, group);
              setDevice(mapped);
              break;
            }
          }
        }
      } catch (error) {
        console.error("Device detail fetch error:", error);
      }
      setLoading(false);
    };

    fetchDevice();
  }, [deviceId, device]);

  // Fetch extra account detail (mail, address, unit, department) from account API
  useEffect(() => {
    if (!device?.accountId) return;

    const fetchAccountDetail = async () => {
      try {
        const response = await (ApiUtils as any).createApiWithToken(false).get(
          (ApiConstant as any).GET_USER,
          HumpUtils.decamelizeKeys({ accountId: device.accountId })
        );
        if (response.status === (ApiConstant as any).STT_OK && response.data) {
          const rawData = HumpUtils.camelizeKeys(response.data);
          const data = rawData.data || rawData;
          setAccountDetail({
            mail: data.mail || data.email,
            address: data.address,
            unitName: data.unitName,
            departmentName: data.departmentName,
            positionName: data.positionName,
          });
        }
      } catch (error) {
        console.error("Account detail fetch error:", error);
      }
    };

    fetchAccountDetail();
  }, [device?.accountId]);

  if (loading) {
    return (
      <div className="d-flex flex-column gap-3">
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-icon btn-outline-secondary" onClick={handleGoBack}>
            <IconArrowLeft size={18} />
          </button>
          <h2 className="mb-0 fw-bold">{t("TXT_DEVICE_DETAIL_TITLE")}</h2>
        </div>
        <div className="card p-5 d-flex justify-content-center align-items-center">
          <CircleLoading />
        </div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="d-flex flex-column gap-3">
        <div className="d-flex align-items-center gap-2">
          <button type="button" className="btn btn-icon btn-outline-secondary" onClick={handleGoBack}>
            <IconArrowLeft size={18} />
          </button>
          <h2 className="mb-0 fw-bold">{t("TXT_DEVICE_DETAIL_TITLE")}</h2>
        </div>
        <div className="card p-5 text-center text-muted">
          {t("TXT_DEVICE_NOT_FOUND")}
        </div>
      </div>
    );
  }

  const isBlocked = device.status === "Bị chặn";
  const isActive = device.status === "Hoạt động";

  const getDeviceIcon = (os: string) => {
    switch (os) {
      case "MacOS":
        return <MacbookDeviceIcon width={60} height={60} />;
      case "Windows":
      case "Linux":
        return <WindowsDeviceIcon width={60} height={60} />;
      case "Android":
      case "iOS":
      default:
        return <MobileDeviceIcon width={60} height={60} />;
    }
  };

  return (
    <div className="d-flex flex-column gap-3" style={{ height: "100%" }}>
      <div className="d-flex align-items-center gap-2">
        <button type="button" className="btn btn-icon btn-outline-secondary" onClick={handleGoBack}>
          <IconArrowLeft size={18} />
        </button>
        <h2 className="mb-0 fw-bold">{t("TXT_DEVICE_DETAIL_TITLE")}</h2>
      </div>

      <div className="row g-3">
        <div className="col-12 col-md-5">
          <div className="card h-100 p-4 d-flex flex-column justify-content-between gap-4">
            <div className="d-flex align-items-start gap-3">
              <div className="d-flex align-items-center justify-content-center flex-shrink-0">
                {getDeviceIcon(device.os)}
              </div>
              <div className="d-flex flex-column gap-1">
                <h3 className="mb-0 fw-bold">{device.deviceName}</h3>
                <div className="d-flex align-items-center gap-2">
                  <span className={`badge ${isBlocked ? "bg-danger" : isActive ? "bg-success" : "bg-secondary"}`}>
                    {device.status}
                  </span>
                </div>
                <span className="text-secondary small">
                  {t("TXT_DEVICE_DETAIL_LAST_ACTIVITY")}:{" "}
                  {isActive ? t("TXT_DEVICE_DETAIL_ACTIVE_NOW") : formatDateTime(device.lastActivity)}
                </span>
              </div>
            </div>

            <div className="border-top pt-3 d-flex flex-column gap-2">
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_ACCOUNT_NAME")}:</span>
                <span className="fw-medium">{device.accountName || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_EMAIL")}:</span>
                <span className="fw-medium">{accountDetail?.mail || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_PHONE")}:</span>
                <span className="fw-medium">{device.phone || "—"}</span>
              </div>
            </div>

            <div className="d-flex gap-2 pt-2">
              {isBlocked ? (
                <button
                  type="button"
                  className="btn btn-success flex-fill"
                  onClick={() => openUnblockDialog(deviceId)}
                >
                  {t("TXT_DEVICE_ACTION_UNBLOCK")}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-danger flex-fill"
                  onClick={() => openBlockDialog(deviceId)}
                >
                  {t("TXT_DEVICE_ACTION_BLOCK")}
                </button>
              )}
              <button
                type="button"
                className="btn btn-warning flex-fill"
                onClick={() => openLogoutDialog(deviceId)}
              >
                {t("TXT_DEVICE_ACTION_LOGOUT")}
              </button>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-7 d-flex flex-column gap-3">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">{t("TXT_DEVICE_DETAIL_INFO")}</h3>
            </div>
            <div className="card-body d-flex flex-column gap-2">
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_DEVICE_NAME")}:</span>
                <span className="fw-medium">{device.deviceName}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_OS")}:</span>
                <span className="fw-medium">{device.os}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_OS_VERSION")}:</span>
                <span className="fw-medium">{device.osVersion || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_APP_VERSION")}:</span>
                <span className="fw-medium">{device.version}</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">{t("TXT_DEVICE_DETAIL_ACCOUNT_INFO")}</h3>
            </div>
            <div className="card-body d-flex flex-column gap-2">
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_ACCOUNT_NAME")}:</span>
                <span className="fw-medium">{device.accountName || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_EMAIL")}:</span>
                <span className="fw-medium">{accountDetail?.mail || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_PHONE")}:</span>
                <span className="fw-medium">{device.phone || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_ADDRESS")}:</span>
                <span className="fw-medium">{accountDetail?.address || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_UNIT")}:</span>
                <span className="fw-medium">{accountDetail?.unitName || "—"}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_LABEL_DEPARTMENT")}:</span>
                <span className="fw-medium">{accountDetail?.departmentName || "—"}</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">{t("TXT_DEVICE_DETAIL_ACTIVITY")}</h3>
            </div>
            <div className="card-body d-flex flex-column gap-2">
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_DETAIL_LAST_ACTIVITY")}:</span>
                <span className="fw-medium">{formatDateTime(device.lastActivity)}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-secondary">{t("TXT_DEVICE_DETAIL_FIRST_LOGIN")}:</span>
                <span className="fw-medium">{formatDateTime(device.firstLoginDate)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        title={getDialogTitle()}
        onDeny={closeDialog}
        onConfirm={() => confirmAction()}
      />
    </div>
  );
};

export default DeviceDetail;
